import { EMPTY_ARRAY } from '@modules/common/constants';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { ShowFeedbackDrawerFromTableOptions } from '@modules/quantify/components/QuantifyReportTableWidget/types';
import {
  useGetMeasuredAsDisplayName,
  useGetQuantifyAggregatePeriod,
  useGetQuantifyCompareQuery,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { useFetchQuantifyFormulas } from '@modules/quantify/hooks/useFetchQuantifyFormulas';
import {
  getDynamicColumns,
  getLabelColumn,
  getQuantifyTableColumnGroupingModel,
  getRadioColumn,
} from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers';
import { getActionsColumn } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/getActionsColumn';
import { getFormulaValueColumn } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/getFormulaValueColumn';
import { useIsAnomaliesTrendVisible } from '@modules/quantify/hooks/useIsAnomaliesTrendVisible';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import { useEffect, useMemo } from 'react';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface useGetNormalAnalysisTableColumnsParams {
  onFeedbackCountClick: (params: ShowFeedbackDrawerFromTableOptions) => void;
  shouldShowRadioColumn?: boolean;
  selectionModel?: string[];
  shouldHideActions?: boolean;
  formulaID?: EQLFunctionName;
  isCompactTableVariant?: boolean;
  shouldShowMergeWithSelectedReasonsAction?: boolean;
}

export function useGetNormalAnalysisTableColumns({
  onFeedbackCountClick,
  shouldShowRadioColumn,
  selectionModel,
  shouldHideActions,
  formulaID,
  isCompactTableVariant,
  shouldShowMergeWithSelectedReasonsAction = true,
}: useGetNormalAnalysisTableColumnsParams): {
  columns: GridColDef[];
  columnGroupingModel: GridColumnGroupingModel;
} {
  const [fetchQuantifyFormulas, { formulas }] = useFetchQuantifyFormulas();
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.formulaQuery?.criteria,
  );
  const compareQuery = useGetQuantifyCompareQuery();
  const activeVisualisationType = useQuantifyStore(
    ({ query }) => query.visualisationType,
  );
  const duration = useQuantifyStore(({ query }) => query.duration);

  const aggregateFormulaResponse = useQuantifyStore(
    ({ reportData }) => reportData.quantify.aggregateFormulaResponse,
  );
  const quantifyDataPoints = useQuantifyStore(
    ({ reportData }) => reportData.quantify.tableRows ?? EMPTY_ARRAY,
  );
  const aggregatePeriod = useGetQuantifyAggregatePeriod();
  const isAnomaliesTrendVisible = useIsAnomaliesTrendVisible();
  const isSingleRecordQuery = useIsSingleRecordQuery({ formulaID });
  const measuredAsDisplayName = useGetMeasuredAsDisplayName({
    formulaID,
  });

  useEffect(() => {
    if (groupBy?.field) {
      fetchQuantifyFormulas(groupBy.field);
    }
  }, [fetchQuantifyFormulas, groupBy?.field]);

  const radioColumn = useMemo(
    () =>
      shouldShowRadioColumn && selectionModel
        ? getRadioColumn({ selectionModel })
        : null,
    [selectionModel, shouldShowRadioColumn],
  );

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const columns = useMemo(() => {
    if (compareQuery) {
      return { columnGroupingModel: [], columns: [] };
    }

    const dataPoints = isSingleRecordQuery
      ? aggregateFormulaResponse
        ? [aggregateFormulaResponse]
        : EMPTY_ARRAY
      : quantifyDataPoints;

    const hasCoOccurringDataPointInData = quantifyDataPoints.some(
      (tableRow) => !tableRow.isRelevant,
    );

    const labelColumn = isSingleRecordQuery
      ? null
      : getLabelColumn({
          groupBy,
          isCompactTableVariant,
          hasCoOccurringDataPointInData,
          shouldRemoveFileUploadPrefix,
          isUnifiedFieldEnabled,
        });

    const actionsColumn = getActionsColumn({
      shouldHideActions,
      isCompactTableVariant,
      shouldShowMergeWithSelectedReasonsAction,
    });

    const formulaValueColumn = getFormulaValueColumn({
      isSingleRecordQuery,
      onFeedbackCountClick,
      columnHeaderName: measuredAsDisplayName,
      groupBy,
      formulaID,
      formulas,
      isCompactTableVariant,
      shouldRemoveFileUploadPrefix,
      isUnifiedFieldEnabled,
      measuredAsCriteria,
    });

    const dynamicColumns = getDynamicColumns({
      onFeedbackCountClick,
      activeVisualisationType,
      aggregatePeriod,
      groupBy,
      dataPoints,
      shouldShowAnomalies: isAnomaliesTrendVisible,
      formulaID,
      measuredAsCriteria,
      shouldRemoveFileUploadPrefix,
      isUnifiedFieldEnabled,
      isSingleRecordQuery,
      duration,
    });

    const columnGroupingModel: GridColumnGroupingModel =
      getQuantifyTableColumnGroupingModel({
        dynamicColumns,
        groupId: measuredAsDisplayName,
      });

    return {
      columns: [
        // Make Formula value as first column in case of aggregate  formula
        isSingleRecordQuery ? formulaValueColumn : null,
        radioColumn,
        labelColumn,
        ...dynamicColumns,
        isSingleRecordQuery ? null : formulaValueColumn,
        actionsColumn,
      ]
        .filter((item): item is GridColDef => !!item)
        .map((column) =>
          isCompactTableVariant ? { ...column, sortable: false } : column,
        ),
      columnGroupingModel,
    };
  }, [
    activeVisualisationType,
    aggregateFormulaResponse,
    aggregatePeriod,
    compareQuery,
    formulaID,
    formulas,
    groupBy,
    isAnomaliesTrendVisible,
    isCompactTableVariant,
    isSingleRecordQuery,
    measuredAsCriteria,
    measuredAsDisplayName,
    onFeedbackCountClick,
    quantifyDataPoints,
    radioColumn,
    shouldHideActions,
    shouldRemoveFileUploadPrefix,
    isUnifiedFieldEnabled,
    shouldShowMergeWithSelectedReasonsAction,
    duration,
  ]);

  return columns;
}
